import {whatsappApi} from '../config/api';
import {formatTime} from './calendarRange';

// WhatsApp notifications for appointments — same message and same microservice
// (ws-send `/send`, sender = the account's linked WhatsApp) as the POS tablets
// (pos_clone services/WhatsAppService.js › buildEventConfirmationMessage).

const LINE = '━━━━━━━━━━━━━━━━━━━━';

const STATUS_TITLES = {
    confirmed: '✅ *Rendez-vous confirmé*',
    pending: '⏳ *Rendez-vous en attente*',
    cancelled: '❌ *Rendez-vous annulé*',
    updated: '🔄 *Rendez-vous modifié*',
};

const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
};

export const buildEventMessage = ({event, professional = '', shopName = '', shopPhone = '', status}) => {
    const client = event.clientName || '';
    const service = (event.selectedServices || []).join(', ');
    const minutes = Math.round((event.end - event.start) / 60000);
    const date = event.start.toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});

    let m = '';
    if (shopName) m += `🏪 *${String(shopName).toUpperCase()}*\n`;
    m += `${LINE}\n${STATUS_TITLES[status || event.status] || STATUS_TITLES.confirmed}\n${LINE}\n`;
    if (client) m += `👤 Bonjour *${client}*,\n\n`;
    m += `📅 *Date* : ${date}\n`;
    m += `🕐 *Heure* : ${formatTime(event.start)} - ${formatTime(event.end)}\n`;
    if (minutes > 0) m += `⏱️ *Durée* : ${formatDuration(minutes)}\n`;
    if (service) m += `💼 *Service* : ${service}\n`;
    if (professional) m += `🧑‍💼 *Avec* : ${professional}\n`;
    m += `${LINE}\n`;
    if (shopPhone) m += `\n📞 Pour annuler ou modifier votre rendez-vous, contactez-nous au *${shopPhone}*.\n`;
    m += `\n🙏 Merci de votre confiance !\nÀ très bientôt.`;
    return m.trim();
};

/**
 * International digits-only number ("212612345678"), or '' when unusable.
 * Local numbers ("0612…") get the client's country code, Morocco by default.
 */
export const normalizeClientPhone = (phone, countryCode) => {
    const raw = String(phone || '').replace(/[\s\-().]/g, '');
    if (!raw) return '';
    let digits;
    if (raw.startsWith('+')) digits = raw.slice(1);
    else if (raw.startsWith('00')) digits = raw.slice(2);
    else {
        const cc = String(countryCode || '212').replace(/\D/g, '');
        digits = raw.startsWith(cc) && raw.length > 10 ? raw : cc + raw.replace(/^0+/, '');
    }
    // "+212 0612…": the national 0 kept after the country code reaches nobody
    digits = digits.replace(/^2120/, '212');
    return /^\d{9,15}$/.test(digits) ? digits : '';
};

/**
 * Send the appointment message to the client.
 * Returns {status: 'sent' | 'no_phone' | 'invalid_phone' | 'failed'}.
 */
export const sendEventWhatsApp = async ({accountId, event, ...messageOptions}) => {
    const phone = event.clientInfo?.phone;
    if (!phone) return {status: 'no_phone'};
    const to = normalizeClientPhone(phone, event.clientInfo?.countryCode);
    if (!to) return {status: 'invalid_phone'};
    try {
        const {data} = await whatsappApi.post('/send', {
            number: accountId,
            message: buildEventMessage({event, ...messageOptions}),
            to,
            type: 'chat',
        });
        return {status: data?.status === true ? 'sent' : 'failed'};
    } catch (error) {
        console.error('[Calendar][WhatsApp] send failed:', error);
        return {status: 'failed'};
    }
};

export const WHATSAPP_FEEDBACK = {
    sent: ['success', 'Message WhatsApp envoyé au client ✓'],
    no_phone: ['warning', "Ce client n'a pas de numéro de téléphone"],
    invalid_phone: ['warning', 'Numéro du client invalide pour WhatsApp'],
    failed: ['error', "Échec de l'envoi WhatsApp (vérifiez la connexion WhatsApp dans Paramètres)"],
};
